import {useCallback, useEffect, useRef, useState} from 'react'
import {useLocation} from 'react-router'

import {useAppDispatch, useAppSelector} from '../../../hooks/react-redux'
import {useSetUserProperty} from '../../../hooks/useSetUserProperty'
import useUID from '../../../hooks/useUID'
import {useBooleanUserProperty} from '../../../hooks/useUserProperty'
import {isAgentsScreen, isQueueScreen} from '../../../routes'
import {getIsSakuraUI} from '../../../selectors'
import {noop} from '../../../utils/empty'
import {useSandbox} from '../../../utils/fastdom'

import {
  COLLAPSED_SIDEBAR_WIDTH,
  CONTENT_WIDTH_WITH_PADDING,
  MIN_NON_COLLAPSED_SIDEBAR_WIDTH,
  SIDEBAR_UNCOLLAPSE_DELTA,
  SIDEBAR_AUTO_COLLAPSE_DELAY,
  MAIN_NAVIGATION_WIDTH,
} from './SidebarPanel.consts'
import {sidebarSettings} from './SidebarPanel.slices'
import type {Props} from './SidebarPanel.types'
import {getSidebarPinnedProperty, SidebarType} from './SidebarPanel.types'
import {
  getCalculatedSidebarWidth,
  getContentTopOffset,
  getDefaultSidebarWidthByWindowWidth,
  getMaxSidebarWidth,
} from './SidebarPanel.utils'

export function useCurrentSidebarId() {
  const location = useLocation()
  if (isAgentsScreen(location.pathname)) {
    return SidebarType.AGENTS
  }
  if (isQueueScreen(location.pathname)) {
    return SidebarType.QUEUE
  }
  return SidebarType.PROJECTS
}

export function useSidebarPinned(sidebarId: SidebarType) {
  return useBooleanUserProperty(getSidebarPinnedProperty(sidebarId), true)
}

const getSidebarState = () => {
  const topOffset = getContentTopOffset()
  let sidebarLeftOffset = MAIN_NAVIGATION_WIDTH
  const scrollLeft = document.documentElement?.scrollLeft

  if (scrollLeft != null && scrollLeft > 0) {
    sidebarLeftOffset -= scrollLeft
  }

  return {
    sidebarFixed: topOffset < 0,
    sidebarTopOffset: topOffset > 0 ? topOffset : 0,
    sidebarLeftOffset,
  }
}

export function useSidebarPanel({offsetBottom = 0, onResize: handleResize = noop}: Props) {
  const sandbox = useSandbox()
  const dispatch = useAppDispatch()
  const isSakura = useAppSelector(getIsSakuraUI)

  const {collapsed: collapsedMap, resizeDelta} = useAppSelector(state => state.sidebarSettings)
  const currentSidebarId = useCurrentSidebarId()
  const isPinned = useSidebarPinned(currentSidebarId)
  const isCollapsed = collapsedMap[currentSidebarId]
  const isOverlayMode = !isPinned

  const [sidebarWidth, setSidebarWidth] = useState(
    getDefaultSidebarWidthByWindowWidth(window.innerWidth) + resizeDelta,
  )
  const [maxSidebarWidth, setMaxSidebarWidth] = useState(getMaxSidebarWidth(isOverlayMode))

  const getCalculatedSidebarWidthWrapper = useCallback(
    (): number =>
      getCalculatedSidebarWidth({
        sidebarCollapsed: isCollapsed,
        maxSidebarWidth,
        sidebarWidth,
      }),
    [maxSidebarWidth, isCollapsed, sidebarWidth],
  )

  const initialState = getSidebarState()
  const [sidebarResizingWidth, setSidebarResizingWidth] = useState<number | null>(null)
  const [sidebarTopOffset, setSidebarTopOffset] = useState(initialState.sidebarTopOffset)
  const [sidebarLeftOffset, setSidebarLeftOffset] = useState(initialState.sidebarLeftOffset)
  const [sidebarFixed, setSidebarFixed] = useState(initialState.sidebarFixed)
  const setUserProperty = useSetUserProperty()

  useEffect(() => {
    const updateSidebarState: () => void = () => {
      const state = getSidebarState()

      setSidebarFixed(state.sidebarFixed)
      setSidebarTopOffset(state.sidebarTopOffset)
      setSidebarLeftOffset(state.sidebarLeftOffset)
    }
    const onWindowResize: () => void = () => {
      setSidebarWidth(getDefaultSidebarWidthByWindowWidth(window.innerWidth) + resizeDelta)
      setMaxSidebarWidth(getMaxSidebarWidth(isOverlayMode))
    }
    document.addEventListener('scroll', updateSidebarState)
    window.addEventListener('resize', onWindowResize)
    updateSidebarState()
    return () => {
      document.removeEventListener('scroll', updateSidebarState)
      window.removeEventListener('resize', onWindowResize)
    }
  }, [resizeDelta, dispatch, isOverlayMode, isPinned])

  useEffect(() => {
    setMaxSidebarWidth(getMaxSidebarWidth(isOverlayMode))
  }, [isOverlayMode])

  useEffect(() => {
    const el = document.querySelector<HTMLElement>('#topWrapper .fixedWidth')

    if (isSakura && el) {
      sandbox.mutate(() => {
        if (isCollapsed) {
          el.style.maxWidth = `${CONTENT_WIDTH_WITH_PADDING + COLLAPSED_SIDEBAR_WIDTH}px`
        } else {
          el.style.maxWidth = `${CONTENT_WIDTH_WITH_PADDING + sidebarWidth}px`
        }
      })
    }
  }, [isSakura, sandbox, isCollapsed, sidebarWidth])

  const onResize: (
    arg0: unknown,
    arg1: unknown,
    arg2: unknown,
    arg3: {
      height: number
      width: number
    },
  ) => void = (event, direction, refToElement, delta) => {
    setSidebarResizingWidth(getCalculatedSidebarWidthWrapper() + delta.width)
  }

  const onResizeStop = (
    event: unknown,
    direction: unknown,
    refToElement: unknown,
    delta: {
      height: number
      width: number
    },
  ) => {
    setSidebarResizingWidth(null)
    if (delta.width !== 0) {
      if (isCollapsed) {
        if (delta.width < SIDEBAR_UNCOLLAPSE_DELTA) {
          return
        }

        let newSidebarWidth
        const width = COLLAPSED_SIDEBAR_WIDTH + delta.width

        if (width > maxSidebarWidth) {
          newSidebarWidth = maxSidebarWidth
        } else {
          newSidebarWidth = sidebarWidth > 0 && sidebarWidth > width ? sidebarWidth : width
        }

        dispatch(
          sidebarSettings.actions.setSidebarCollapsed({id: currentSidebarId, collapsed: false}),
        )
        dispatch(
          sidebarSettings.actions.setResizeDelta(
            newSidebarWidth - getDefaultSidebarWidthByWindowWidth(window.innerWidth),
          ),
        )
        setSidebarWidth(newSidebarWidth)
        return
      }

      let newSidebarWidth = getCalculatedSidebarWidthWrapper() + delta.width

      if (newSidebarWidth >= MIN_NON_COLLAPSED_SIDEBAR_WIDTH) {
        if (newSidebarWidth > maxSidebarWidth) {
          newSidebarWidth = maxSidebarWidth
        }

        dispatch(
          sidebarSettings.actions.setResizeDelta(
            newSidebarWidth - getDefaultSidebarWidthByWindowWidth(window.innerWidth),
          ),
        )
        setSidebarWidth(newSidebarWidth)
        return
      }

      dispatch(sidebarSettings.actions.setSidebarCollapsed({id: currentSidebarId, collapsed: true}))
      setSidebarResizingWidth(null)
    }
  }

  const toggleCollapseSidebar = useCallback(() => {
    dispatch(sidebarSettings.actions.toggleCollapseSidebar(currentSidebarId))
  }, [dispatch, currentSidebarId])

  const togglePinSidebar = useCallback(() => {
    setUserProperty(getSidebarPinnedProperty(currentSidebarId), String(!isPinned))
    dispatch(
      sidebarSettings.actions.setSidebarCollapsed({id: currentSidebarId, collapsed: isPinned}),
    )
  }, [dispatch, currentSidebarId, isPinned, setUserProperty])

  const ref = useSidebarOverlayMode(isOverlayMode, isCollapsed, currentSidebarId)

  useEffect(() => {
    if (sidebarResizingWidth != null && sidebarResizingWidth < MIN_NON_COLLAPSED_SIDEBAR_WIDTH) {
      dispatch(sidebarSettings.actions.setSidebarCollapsed({id: currentSidebarId, collapsed: true}))
    }
  }, [sidebarResizingWidth, dispatch, currentSidebarId])

  const calculatedSidebarWidth = getCalculatedSidebarWidthWrapper()
  const sidebarHeight = `calc(100vh - ${offsetBottom}px - ${sidebarTopOffset > 0 ? sidebarTopOffset + 1 : 0}px)`

  useEffect(() => {
    handleResize(sidebarResizingWidth ?? calculatedSidebarWidth ?? 0)
    return () => handleResize(0)
  }, [handleResize, calculatedSidebarWidth, sidebarResizingWidth, sidebarTopOffset])

  const shortcutsScope = useUID('overview-')
  const shortcutsMap = {
    s: !isOverlayMode ? noop : toggleCollapseSidebar,
    'alt+s': togglePinSidebar,
  }

  return {
    ref,
    shortcutsScope,
    shortcutsMap,
    sidebarCollapsed: isCollapsed,
    sidebarFixed,
    sidebarOverlayMode: isOverlayMode,
    sidebarTopOffset,
    sidebarHeight,
    sidebarLeftOffset,
    calculatedSidebarWidth,
    maxSidebarWidth,
    onResize,
    onResizeStop,
  }
}

function useSidebarOverlayMode(
  isOverlayMode: boolean,
  collapsed: boolean,
  currentSidebarId: SidebarType,
) {
  const ref = useRef<HTMLDivElement>(null)
  const timer = useRef<number | null>(null)

  const dispatch = useAppDispatch()
  const isAutoCollapseDisabled = useAppSelector(state => state.sidebarSettings.autoCollapseDisabled)

  if (collapsed && isAutoCollapseDisabled) {
    dispatch(sidebarSettings.actions.setAutoCollapseDisabled(false))
  }

  const onDocumentClick = useCallback(
    (event: MouseEvent) => {
      if (ref.current && event.target instanceof Node && ref.current.contains(event.target)) {
        return
      }
      dispatch(sidebarSettings.actions.setSidebarCollapsed({id: currentSidebarId, collapsed: true}))
    },
    [dispatch, currentSidebarId],
  )

  const onMouseLeave = useCallback(() => {
    timer.current = window.setTimeout(() => {
      dispatch(sidebarSettings.actions.setSidebarCollapsed({id: currentSidebarId, collapsed: true}))
    }, SIDEBAR_AUTO_COLLAPSE_DELAY)
  }, [dispatch, currentSidebarId])

  const clearTimer = useCallback(() => {
    if (timer.current != null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
  }, [])

  useEffect(() => {
    if (!isOverlayMode || collapsed) {
      return undefined
    }

    const sidebarRef = ref.current
    if (!isAutoCollapseDisabled) {
      sidebarRef?.addEventListener('mouseenter', clearTimer)
      sidebarRef?.addEventListener('mouseleave', onMouseLeave)
    }
    document.addEventListener('click', onDocumentClick)

    return () => {
      clearTimer()
      sidebarRef?.removeEventListener('mouseenter', clearTimer)
      sidebarRef?.removeEventListener('mouseleave', onMouseLeave)
      document.removeEventListener('click', onDocumentClick)
    }
  }, [isOverlayMode, collapsed, isAutoCollapseDisabled, onDocumentClick, clearTimer, onMouseLeave])

  return ref
}
